/* eslint-disable no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  BaseAutoTracker,
  IAutoTrackerConfig,
  ICapturedEvent,
} from './BaseAutoTracker';
import { SelectorUtils } from './utils/SelectorUtils';
import { EventManager } from './utils/EventManager';
import { GomarkePixelConfig, PixelResponse } from '@/types';
import { LeadTracker } from '@/conversion/tracks/lead';
import type { TrackConversionFn } from '../engine';
import type { ILeadData } from '@/conversion/tracks/lead';

/**
 * Configuração específica para autoLead
 */
export interface IAutoLeadConfig extends IAutoTrackerConfig {
  extractFormData?: boolean;
  formFieldSelectors?: {
    email?: string;
    name?: string;
    phone?: string;
    message?: string;
  };
  customFormExtractor?: (form: HTMLFormElement) => {
    email?: string;
    name?: string;
    phone?: string;
    message?: string;
  };
  trackOnFieldFocus?: boolean;
  trackOnFieldBlur?: boolean;
  trackOnFieldChange?: boolean;
  requiredFields?: string[];
  validationRules?: {
    email?: RegExp;
    phone?: RegExp;
    minLength?: number;
  };
}

/**
 * AutoTracker para eventos de lead
 */
export class AutoLeadTracker extends BaseAutoTracker {
  private leadTracker: LeadTracker;
  private eventManager: EventManager;
  private autoConfig?: IAutoLeadConfig;
  private trackConversion?: TrackConversionFn;
  private fieldListeners: Map<string, (event: Event) => Promise<void> | void> =
    new Map();

  constructor(
    config: GomarkePixelConfig,
    sessionId: string,
    version: string,
    visitorId: string = '',
    trackConversion?: TrackConversionFn
  ) {
    super(config, sessionId, version, visitorId);
    this.trackConversion = trackConversion;
    this.leadTracker = new LeadTracker(config, sessionId, visitorId, version);
    this.eventManager = new EventManager({
      debounceMs: 1000,
      throttleMs: 5000,
      enableLogging: config.debug,
    });
  }

  private trackLead(leadData: Record<string, string>): Promise<PixelResponse> {
    if (this.trackConversion) {
      return this.trackConversion('lead', leadData as ILeadData);
    }
    return this.leadTracker.track(leadData);
  }

  /**
   * Inicializa o autoTracker
   */
  public async initialize(autoConfig: IAutoLeadConfig): Promise<void> {
    if (!autoConfig.isEnabled) {
      this.log('AutoLead desabilitado');
      return;
    }

    this.autoConfig = autoConfig;
    this.log(`Inicializando AutoLead com seletor: ${autoConfig.selector}`);

    // Aguarda DOM estar pronto
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () =>
        this.setupListeners()
      );
    } else {
      await this.setupListeners();
    }

    // Configura tracking de campos se habilitado
    if (
      autoConfig.trackOnFieldFocus ||
      autoConfig.trackOnFieldBlur ||
      autoConfig.trackOnFieldChange
    ) {
      this.setupFieldTracking();
    }

    // Observa mudanças no DOM para elementos dinâmicos
    this.observeDOM(() => this.setupListeners());

    this.isInitialized = true;
  }

  /**
   * Configura os listeners
   */
  private async setupListeners(): Promise<void> {
    if (!this.autoConfig) return;

    // Limpa listeners existentes
    this.clearListeners();

    try {
      const elements = await SelectorUtils.findElementsWithRetry(
        this.autoConfig.selector,
        this.autoConfig.selectorType,
        3,
        1000
      );

      this.log(`Encontrados ${elements.length} elementos para Lead`);

      elements.forEach(element => {
        // Converte Node para Element
        const htmlElement = element as Element;

        // Listener para submit de formulário
        if (htmlElement.tagName.toLowerCase() === 'form') {
          this.addListenerWithTiming(
            htmlElement,
            'submit',
            this.handleFormSubmit.bind(this),
            this.autoConfig!
          );
        } else {
          // Listener para clique em botões/links
          this.addListenerWithTiming(
            htmlElement,
            'click',
            this.handleLeadClick.bind(this),
            this.autoConfig!
          );
        }
      });
    } catch (error) {
      this.log(`Erro ao configurar listeners: ${error}`);
    }
  }

  /**
   * Configura tracking de campos de formulário
   */
  private setupFieldTracking(): void {
    if (!this.autoConfig) return;

    const formElements = document.querySelectorAll('form');

    formElements.forEach(form => {
      const fields = form.querySelectorAll('input, textarea, select');

      fields.forEach(field => {
        const fieldName =
          field.getAttribute('name') || field.getAttribute('id') || 'unknown';

        if (this.autoConfig?.trackOnFieldFocus) {
          const focusListener = () => this.handleFieldFocus(field);
          this.fieldListeners.set(`${fieldName}_focus`, focusListener);
          field.addEventListener('focus', focusListener);
        }

        if (this.autoConfig?.trackOnFieldBlur) {
          const blurListener = () => this.handleFieldBlur(field);
          this.fieldListeners.set(`${fieldName}_blur`, blurListener);
          field.addEventListener('blur', blurListener);
        }

        if (this.autoConfig?.trackOnFieldChange) {
          const changeListener = () => this.handleFieldChange(field);
          this.fieldListeners.set(`${fieldName}_change`, changeListener);
          field.addEventListener('change', changeListener);
        }
      });
    });
  }

  /**
   * Manipula submit de formulário
   */
  private async handleFormSubmit(
    capturedEvent: ICapturedEvent
  ): Promise<PixelResponse> {
    this.log('Lead form submit detectado', capturedEvent);

    const form = capturedEvent.element as HTMLFormElement;
    const formData = this.extractFormData(form);

    // Valida dados se necessário
    if (this.autoConfig?.requiredFields && !this.validateFormData(formData)) {
      this.log('Dados do formulário inválidos');
      return Promise.resolve({
        success: false,
        error: 'Dados obrigatórios ausentes',
        timestamp: Date.now(),
      });
    }

    return this.trackLead(formData);
  }

  /**
   * Manipula clique em lead
   */
  private async handleLeadClick(
    capturedEvent: ICapturedEvent
  ): Promise<PixelResponse> {
    this.log('Lead click detectado', capturedEvent);

    const target = capturedEvent.event.target as Element;
    if (!target) {
      return this.trackLead({});
    }

    const form = this.resolveFormFromClick(target);

    // 🔥 Se encontrou form → usa o MESMO pipeline do submit
    if (form) {
      const leadData = this.extractFormData(form);
      return this.trackLead(leadData);
    }

    // ⚠️ Sem form → fallback mínimo
    return this.trackLead({});
  }

  /**
   * Manipula foco em campo
   */
  private async handleFieldFocus(field: Element): Promise<void> {
    this.log('Campo focado', field);
    // Implementar tracking de foco se necessário
  }

  /**
   * Manipula blur em campo
   */
  private async handleFieldBlur(field: Element): Promise<void> {
    this.log('Campo perdeu foco', field);
    // Implementar tracking de blur se necessário
  }

  /**
   * Manipula mudança em campo
   */
  private async handleFieldChange(field: Element): Promise<void> {
    this.log('Campo alterado', field);
    // Implementar tracking de mudança se necessário
  }

  /**
   * Extrai dados do formulário
   */
  private extractFormData(form: HTMLFormElement): Record<string, any> {
    // 1. Base: todos os campos do form
    const data: Record<string, any> = this.extractAllFormFields(form);

    // 2. Config por selectors (override pontual)
    if (this.autoConfig?.formFieldSelectors) {
      const selectors = this.autoConfig.formFieldSelectors;

      if (selectors.email)
        data.email = this.getFieldValue(form, selectors.email) || data.email;

      if (selectors.name)
        data.name = this.getFieldValue(form, selectors.name) || data.name;

      if (selectors.phone)
        data.phone = this.getFieldValue(form, selectors.phone) || data.phone;

      if (selectors.message)
        data.message =
          this.getFieldValue(form, selectors.message) || data.message;
    }

    // 3. Extractor custom (override total)
    if (this.autoConfig?.customFormExtractor) {
      const customData = this.autoConfig.customFormExtractor(form);
      Object.assign(data, customData);
    }

    return data;
  }

  /**
   * Obtém valor de campo usando seletor
   */
  private getFieldValue(
    form: HTMLFormElement,
    selector?: string
  ): string | null {
    if (!selector) return null;

    const field = form.querySelector(`${selector}`) as
      | HTMLInputElement
      | HTMLTextAreaElement
      | HTMLSelectElement;
    return field?.value || null;
  }

  private resolveFormFromClick(target: Element): HTMLFormElement | null {
    // 1. Se o botão estiver dentro de um form
    const form = target.closest('form');
    if (form) return form;

    // 2. Botão com atributo que aponta para form
    if (target instanceof HTMLButtonElement && target.form) {
      return target.form;
    }

    // 3. Custom attribute (power user)
    const formSelector = target.getAttribute('data-lead-form');
    if (formSelector) {
      return document.querySelector(formSelector);
    }

    return null;
  }

  private extractAllFormFields(form: HTMLFormElement): Record<string, any> {
    const data: Record<string, any> = {};

    const formData = new FormData(form);

    for (const [key, value] of formData.entries()) {
      // múltiplos campos com mesmo name (checkbox, multi-select)
      if (key in data) {
        if (Array.isArray(data[key])) {
          data[key].push(value);
        } else {
          data[key] = [data[key], value];
        }
      } else {
        data[key] = value;
      }
    }

    return data;
  }

  /**
   * Valida dados do formulário
   */
  private validateFormData(data: Record<string, string>): boolean {
    if (!this.autoConfig?.requiredFields) return true;

    const requiredFields = this.autoConfig.requiredFields;

    for (const field of requiredFields) {
      if (!data[field as keyof typeof data]) {
        return false;
      }
    }

    // Validação com regras customizadas
    if (this.autoConfig?.validationRules) {
      const rules = this.autoConfig.validationRules;

      if (rules.email && data.email && !rules.email.test(data.email)) {
        return false;
      }

      if (rules.phone && data.phone && !rules.phone.test(data.phone)) {
        return false;
      }

      if (
        rules.minLength &&
        data.message &&
        data.message.length < rules.minLength
      ) {
        return false;
      }
    }

    return true;
  }

  /**
   * Para o autoTracker
   */
  public stop(): void {
    this.clearListeners();
    this.clearFieldListeners();
    this.eventManager.clear();
    this.isInitialized = false;
    this.log('AutoLead parado');
  }

  /**
   * Limpa listeners de campos
   */
  private clearFieldListeners(): void {
    this.fieldListeners.forEach((listener, key) => {
      const [fieldName, eventType] = key.split('_');
      const fields = document.querySelectorAll(
        `[name="${fieldName}"], [id="${fieldName}"]`
      );
      fields.forEach(field => {
        field.removeEventListener(eventType, listener);
      });
    });
    this.fieldListeners.clear();
  }

  /**
   * Reconstrói os listeners
   */
  public async rebuild(): Promise<void> {
    if (this.autoConfig) {
      await this.setupListeners();
      if (
        this.autoConfig.trackOnFieldFocus ||
        this.autoConfig.trackOnFieldBlur ||
        this.autoConfig.trackOnFieldChange
      ) {
        this.setupFieldTracking();
      }
    }
  }

  /**
   * Obtém estatísticas específicas
   */
  public getLeadStats(): {
    baseStats: unknown;
    eventStats: unknown;
    fieldListeners: number;
  } {
    return {
      baseStats: {},
      eventStats: this.eventManager.getStats(),
      fieldListeners: this.fieldListeners.size,
    };
  }

  /**
   * Log de eventos
   */
  private log(message: string, data?: unknown): void {
    if (this.baseConfig.debug) {
      console.log(`[AutoLead] ${message}`, data);
    }
  }
}

// Função de conveniência para compatibilidade
export const autoLead = (_config: IAutoLeadConfig): void => {
  console.warn('autoLead: Use a classe AutoLeadTracker diretamente');
};
